import type { ReferenceId, ReferenceKind } from "@ctrl/controller-rpc";

/**
 * Convert Routine "internal" links to an in-app router path.
 *
 * Supported:
 * - `https://app.routine.co/PATH?QUERY#HASH`
 * - `routine://PATH?QUERY#HASH` (deep link)
 * - `https://open.routine.co?path=PATH?QUERY#HASH`
 * - `https://open.routine.co/PATH?QUERY#HASH`
 */
export function internalPathFromRoutineHref(href: string): string | null {
  // Already-internal paths
  if (href.startsWith("/")) return href;

  let url: URL;
  try {
    url = new URL(href);
  } catch {
    return null;
  }

  // Deep links like `routine://tasks/123` should be routed internally.
  // Note: `new URL("routine://tasks/123")` yields host="tasks" and pathname="/123".
  if (url.protocol === "routine:") {
    const hostPart = url.host ? `/${url.host}` : "";
    const pathPart = url.pathname || "";
    const internal = `${hostPart}${pathPart}` || "/";
    return `${internal}${url.search}${url.hash}`;
  }

  const host = url.host.toLowerCase();

  // Web app links that point back to the app should be handled by the router.
  if (host === "app.routine.co") {
    return `${url.pathname}${url.search}${url.hash}`;
  }

  // "Open in app" style links.
  if (host === "open.routine.co") {
    const pathParam = url.searchParams.get("path");
    if (pathParam) {
      const path = pathParam.startsWith("/") ? pathParam : `/${pathParam}`;
      return `${path}${url.hash}`;
    }
    // Links can also use a regular pathname (e.g. https://open.routine.co/tasks/123?x=y#block=abc)
    if (url.pathname && url.pathname !== "/") {
      return `${url.pathname}${url.search}${url.hash}`;
    }
    // Some links might store the internal path in the hash (e.g. "#/tasks/...")
    if (url.hash.startsWith("#/")) {
      return url.hash.slice(1);
    }
  }

  return null;
}

/**
 * Recognize a string that is a Routine reference id (e.g. `event:abc:2026-05-11`,
 * `task:xyz`, `object:workspace:table:row`) and return the corresponding
 * `ReferenceId`.
 *
 * Order matters: longer, more specific prefixes are checked first so that
 * `event-rec:` is not mistakenly classified as `event:`.
 */
const KIND_PREFIXES: ReadonlyArray<{ prefix: string; kind: ReferenceKind }> = [
  { prefix: "automation_run_ref:", kind: "automation_run" },
  { prefix: "conversation_ref:", kind: "conversation" },
  { prefix: "automation_ref:", kind: "automation" },
  { prefix: "agent_ref:", kind: "agent" },
  { prefix: "task-rec:", kind: "task-rec" },
  { prefix: "event-rec:", kind: "event-rec" },
  { prefix: "workspace_ref:", kind: "workspace_ref" },
  { prefix: "table_ref:", kind: "table_ref" },
  { prefix: "event:", kind: "event" },
  { prefix: "task:", kind: "task" },
  { prefix: "page:", kind: "page" },
  { prefix: "people:", kind: "people" },
  { prefix: "object:", kind: "object" },
  { prefix: "org:", kind: "organization" },
];

export function referenceIdFromString(s: string): ReferenceId | null {
  for (const { prefix, kind } of KIND_PREFIXES) {
    if (s.startsWith(prefix)) {
      // Keep the full id (including the kind prefix); this matches what
      // `controller.<kind>.get` and the `:id` route param expect.
      return { kind, id: s };
    }
  }
  return null;
}

/**
 * Routine record ids (`automation:…`, `object:…`, …) are valid URIs, so
 * handing them to the OS produces macOS's "no application set to open the
 * URL" dialog. Longer prefixes are listed first so `automation-run:` is not
 * classified as `automation:`.
 */
const ROUTINE_ID_PREFIXES = [
  "automation-run:",
  "task-rec:",
  "event-rec:",
  "workspace_ref:",
  "table_ref:",
  "automation:",
  "assistant:",
  "conversation:",
  "activity:",
  "agent:",
  "object:",
  "event:",
  "task:",
  "page:",
  "people:",
  "workspace:",
  "org:",
] as const;

export function isRoutinePrefixedId(s: string): boolean {
  return ROUTINE_ID_PREFIXES.some((prefix) => s.startsWith(prefix));
}

/**
 * Map a raw Routine id that is not a graph `ReferenceId` onto an in-app path.
 * Returns null when the id has no dedicated screen (or needs more context).
 */
export function internalPathFromRoutineId(s: string): string | null {
  if (s.startsWith("automation-run:")) return null;
  if (s.startsWith("automation:")) return `/automations/${s}`;
  if (s.startsWith("agent:") || s.startsWith("assistant:")) {
    return `/agents/${s}`;
  }
  // Longer prefixes first: `event-rec:` would otherwise match `event:`.
  // Recurrent events are `ReferenceId`s and go through `navigateToObject`.
  if (s.startsWith("event-rec:")) return null;
  if (s.startsWith("event:")) return `/events/${s}`;
  if (s.startsWith("page:")) return `/pages/${s}`;
  if (s.startsWith("people:")) return `/people/${s}`;
  // Organization.Id in `api/schemas/src/ids.ml` uses the `org` prefix.
  if (s.startsWith("org:")) return `/organizations/${s}`;
  return null;
}

/**
 * Extract a block id from a fragment identifier.
 *
 * Supported:
 * - `#block=<id>` (query-string style fragment)
 */
export function blockIdFromHash(hash: string): string | null {
  if (!hash) return null;
  const raw = hash.startsWith("#") ? hash.slice(1) : hash;
  if (!raw) return null;

  // Only support query-string style fragments like `#block=<id>`.
  // Note: the ID itself may contain ':' (e.g. `block:...`) and will still parse.
  const params = new URLSearchParams(raw);
  return params.get("block");
}
